import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Search, Download } from 'lucide-react';
import { api } from '../../services/api';
import { fmtMoney, fmtNumber, fmtPct } from './format';
import { tbl } from '../../theme';

const PAGE = 100;

/**
 * OtherDetail — the contents of a chart's "Other" bar, itemized.
 *
 * Exists because "Other" is not a rounding error: on the vendor dimension it is
 * $1.15M across 2,754 merchants, larger than every named vendor put together.
 * A bar that dwarfs the ones it sits beside has to be inspectable, or the chart
 * is actively misleading about where the money went.
 *
 * Rows are fetched with an offset past the charted head, so this lists exactly
 * what the bar aggregates — nothing more, nothing less.
 */
export default function OtherDetail({
  open, onClose, dimension, dimensionLabel, filterParams, headCount, unitLabel = 'values',
  totalAmount, totalCount, onDrill,
}) {
  const [page, setPage]     = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => { if (open) { setPage(0); setSearch(''); } }, [open, dimension]);

  const { data, isFetching } = useQuery({
    queryKey: ['expense-other-detail', dimension, filterParams, headCount, page],
    queryFn:  () => api.get('/expenses/breakdown', {
      params: { ...filterParams, dimension, limit: PAGE, offset: headCount + page * PAGE },
    }),
    enabled: open,
    keepPreviousData: true,
  });

  const rows = data?.rows || [];
  const remainingBelow = data?.remainder_count ?? 0;

  // Search filters the CURRENT page only — stated in the UI rather than
  // implied, so nobody reads an empty result as "this vendor does not exist".
  const shown = useMemo(() => {
    const t = search.trim().toLowerCase();
    return t ? rows.filter((r) => String(r.label).toLowerCase().includes(t)) : rows;
  }, [rows, search]);

  if (!open) return null;

  const from = headCount + page * PAGE + 1;
  const to   = headCount + page * PAGE + rows.length;

  function exportCsv() {
    const header = [dimensionLabel, 'Net Amount', 'Transactions', 'Share of selection'];
    const body = shown.map((r) => [r.label, r.net_amount, r.transaction_count, (r.share * 100).toFixed(3) + '%']);
    const csv = [header, ...body]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `other-${dimension}-${from}-${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.head}>
          <div>
            <div style={s.title}>Inside “Other” — {dimensionLabel}</div>
            <div style={s.sub}>
              {fmtNumber(totalCount)} {unitLabel} totaling {fmtMoney(totalAmount)} — everything below the
              top {headCount} charted {unitLabel}.
            </div>
          </div>
          <button style={s.close} onClick={onClose} aria-label="Close"><X size={17} /></button>
        </div>

        <div style={s.toolbar}>
          <div style={s.searchWrap}>
            <Search size={13} color="#9CA3AF" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder={`Search this page of ${unitLabel}…`} style={s.searchInput} />
          </div>
          <span style={s.range}>
            {rows.length ? `Ranked ${fmtNumber(from)}–${fmtNumber(to)}` : '—'}
            {search && ` · ${shown.length} match this page`}
          </span>
          <div style={{ flex: 1 }} />
          <button style={s.csvBtn} onClick={exportCsv} disabled={!shown.length}>
            <Download size={12} /> CSV
          </button>
        </div>

        <div style={{ ...s.tableWrap, opacity: isFetching ? 0.6 : 1 }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: 52 }}>#</th>
                <th style={s.th}>{dimensionLabel}</th>
                <th style={{ ...s.th, textAlign: 'right', width: 110 }}>Amount</th>
                <th style={{ ...s.th, textAlign: 'right', width: 92 }}>Txns</th>
                <th style={{ ...s.th, textAlign: 'right', width: 84 }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && !isFetching && (
                <tr><td colSpan={5} style={s.empty}>
                  {search ? 'No matches on this page — try the next page or clear the search.' : 'Nothing here.'}
                </td></tr>
              )}
              {shown.map((r, i) => (
                <tr key={r.label} style={s.tr}>
                  <td style={{ ...s.td, color: '#9CA3AF' }}>
                    {fmtNumber(headCount + page * PAGE + rows.indexOf(r) + 1)}
                  </td>
                  <td style={s.td}>
                    {onDrill
                      ? <button style={s.linkBtn} onClick={() => { onDrill(dimension, r.label); onClose(); }}>{r.label}</button>
                      : r.label}
                  </td>
                  <td style={s.tdNum}>{fmtMoney(r.net_amount)}</td>
                  <td style={s.tdNum}>{fmtNumber(r.transaction_count)}</td>
                  <td style={{ ...s.tdNum, color: '#9CA3AF' }}>{fmtPct(r.share, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={s.footer}>
          <button style={s.pageBtn} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button>
          <span style={s.footInfo}>
            {remainingBelow > 0
              ? `${fmtNumber(remainingBelow)} more ${unitLabel} below`
              : 'End of list'}
          </span>
          <button style={s.pageBtn} disabled={remainingBelow <= 0} onClick={() => setPage((p) => p + 1)}>Next →</button>
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', zIndex: 200,
             display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  modal:   { background: '#fff', borderRadius: '12px', width: 'min(880px, 100%)', maxHeight: '86vh',
             display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
             padding: '16px 18px 12px', borderBottom: '1px solid #E5E7EB' },
  title:   { fontSize: '15px', fontWeight: 600, color: '#111827' },
  sub:     { fontSize: '11.5px', color: '#6B7280', marginTop: '3px', maxWidth: '620px', lineHeight: 1.5 },
  close:   { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: '2px' },
  toolbar: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 18px', borderBottom: '1px solid #F3F4F6' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: '6px', border: '1px solid #E5E7EB',
                borderRadius: '6px', padding: '0 8px', height: '30px', width: '260px' },
  searchInput:{ flex: 1, border: 'none', outline: 'none', fontSize: '12px' },
  range:   { fontSize: '11px', color: '#6B7280' },
  csvBtn:  { display: 'inline-flex', alignItems: 'center', gap: '5px', height: '30px', padding: '0 10px',
             border: '1px solid #D1D5DB', borderRadius: '6px', background: '#fff', fontSize: '11px', cursor: 'pointer' },
  tableWrap:{ overflowY: 'auto', flex: 1, transition: 'opacity 0.15s' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { padding: '8px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', position: 'sticky', top: 0,
        fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px',
        color: '#6B7280', textAlign: 'left', whiteSpace: 'nowrap' },
  tr:    { ...tbl.tr, ...tbl.tr },
  td:    { ...tbl.td, ...tbl.tdSm },
  tdNum: { ...tbl.tdR, ...tbl.tdRSm },
  linkBtn:{ background: 'none', border: 'none', padding: 0, color: '#1D4ED8', cursor: 'pointer',
            fontSize: '12px', textAlign: 'left', fontFamily: 'inherit' },
  empty: { padding: '32px', textAlign: 'center', color: '#9CA3AF', fontSize: '12px' },
  footer:{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
           padding: '10px', borderTop: '1px solid #E5E7EB' },
  pageBtn:{ padding: '5px 12px', fontSize: '11px', border: '1px solid #E5E7EB', borderRadius: '6px',
            background: '#fff', cursor: 'pointer' },
  footInfo:{ fontSize: '11px', color: '#6B7280' },
};
